import * as THREE from 'three';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CuboidProps, Number3, TransformTarget } from '../../types';
import { Cuboid } from '../Cuboid';
import { type CuboidEditorProps } from './types';
import { TransformControls } from '../TransformControls';
import { TransformMode, ControlsType, TransformControlsMode, TransformSpace } from '../Enums';
import { VisibilityBoxControl } from '../VisibilityBoxControl';
import {
  getCurrentTransformMatrix,
  isSameTransform,
  positionRotationScaleFromMatrix4,
} from '../Util';
import type { Transform, VisibilityBoxControlStyle } from '../VisibilityBox';

const COORD_STEP = 0.001;

const defaultAlignedBoxControlsStyle: VisibilityBoxControlStyle = {
  boxFrame: { color: 0x00ff00, lineWidth: 1 },
  draggingBox: { color: 0x00ff00, draggingColor: 0xff2222, size: 3 },
};

const transformModeMapping = new Map<
  TransformMode,
  [type: ControlsType, mode?: TransformControlsMode]
>([
  [TransformMode.View, [ControlsType.View]],
  [TransformMode.BoxResize, [ControlsType.AlignedBox]],
  [TransformMode.Translate, [ControlsType.Transform, TransformControlsMode.Translate]],
  [TransformMode.Rotate, [ControlsType.Transform, TransformControlsMode.Rotate]],
  [TransformMode.Scale, [ControlsType.Transform, TransformControlsMode.Scale]],
]);

export const CuboidEditor = <CuboidT extends CuboidProps>({
  isEnabled,
  cuboid,
  setCuboid,
  cuboidStyle,
  transformMode,
  transformSpace = TransformSpace.Local,
  onSaveCuboid,
  step,
  alignedBoxControlsStyle = defaultAlignedBoxControlsStyle,
}: CuboidEditorProps<CuboidT>) => {
  const [transformTarget, setTransformTarget] = useState<TransformTarget>();

  const [targetGroup, setTargetGroup] = useState<THREE.Group>();

  const targetGroupCallback = useCallback((group: THREE.Group) => {
    setTargetGroup(group);
  }, []);

  const boxStep: Number3 = useMemo(() => {
    return step || [COORD_STEP, COORD_STEP, COORD_STEP];
  }, [step]);

  useEffect(() => {
    if (targetGroup) {
      setTransformTarget({ targetGroup, leafGroup: targetGroup });
    } else {
      setTransformTarget(undefined);
    }
  }, [targetGroup]);

  const [controlsType, transformControlsMode] = useMemo(
    () => transformModeMapping.get(transformMode) ?? [ControlsType.View],
    [transformMode]
  );

  const previousMode = useRef<
    { wasEnabled: boolean; hadControlsType: ControlsType; hadCuboid: CuboidT } | undefined
  >();

  useEffect(() => {
    const { wasEnabled, hadControlsType, hadCuboid } = previousMode.current ?? {};
    if (
      wasEnabled === isEnabled &&
      hadControlsType === controlsType &&
      hadCuboid?.id === cuboid.id
    ) {
      return;
    }

    previousMode.current = {
      wasEnabled: isEnabled,
      hadControlsType: controlsType,
      hadCuboid: cuboid,
    };

    const wasInEditMode = !!(
      wasEnabled &&
      hadCuboid &&
      hadControlsType !== undefined &&
      hadControlsType !== ControlsType.View
    );

    const isInEditMode = !!(
      isEnabled &&
      cuboid &&
      controlsType !== undefined &&
      controlsType !== ControlsType.View
    );

    const shouldSaveCuboid =
      (wasInEditMode && !isInEditMode) || (wasInEditMode && cuboid.id !== hadCuboid.id);
    if (shouldSaveCuboid) {
      const cuboidToSave = cuboid.id === hadCuboid?.id ? cuboid : hadCuboid;
      if (cuboidToSave) {
        onSaveCuboid(cuboidToSave);
      }
    }
  }, [isEnabled, controlsType, cuboid, onSaveCuboid]);

  const box = useMemo(() => {
    if (!cuboid) return undefined;
    if (controlsType !== ControlsType.View && controlsType !== ControlsType.AlignedBox) {
      return undefined;
    }
    const { position, rotation, scale } = cuboid;
    return { position, rotation, scale };
  }, [cuboid, controlsType]);

  const setBox = useCallback(
    (box: Transform) => {
      const { position, rotation, scale } = box;
      setCuboid((current) => {
        if (!current) return undefined;
        if (isSameTransform(current, { position, rotation, scale })) return current;
        return { ...current, position, rotation, scale };
      });
    },
    [setCuboid]
  );

  const onChange = useCallback(() => {
    if (!transformTarget) return;
    const matrix4 = getCurrentTransformMatrix(transformTarget);
    const { position, rotation, scale } = positionRotationScaleFromMatrix4(matrix4);
    setCuboid((current) => {
      if (!current) return undefined;
      if (isSameTransform(current, { position, rotation, scale })) return current;
      return { ...current, position, rotation, scale };
    });
  }, [transformTarget, setCuboid]);

  const { position, rotation, scale, style, hoveredStyle, ...restCuboidProps } = cuboid;

  return (
    <>
      {isEnabled && (
        <>
          {!box && (
            <>
              <group
                ref={targetGroupCallback}
                position={position}
                rotation={rotation}
                scale={scale}
              >
                <Cuboid
                  position={[0, 0, 0]}
                  rotation={[0, 0, 0]}
                  scale={[1, 1, 1]}
                  style={cuboidStyle ?? hoveredStyle ?? style}
                  {...restCuboidProps}
                />
              </group>
              <TransformControls
                target={controlsType === ControlsType.Transform ? transformTarget : undefined}
                mode={transformControlsMode ?? TransformControlsMode.Translate}
                space={transformSpace}
                size={2}
                onChange={onChange}
              />
            </>
          )}
          {box && (
            <>
              <Cuboid
                position={position}
                rotation={rotation}
                scale={scale}
                style={cuboidStyle ?? hoveredStyle ?? style}
                {...restCuboidProps}
              />
              {controlsType === ControlsType.AlignedBox && (
                <VisibilityBoxControl
                  box={box}
                  setBox={setBox}
                  step={boxStep}
                  style={alignedBoxControlsStyle}
                  showWireframe={false}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
